import type { ReactNode, PointerEvent } from 'react';

type Panel = 'LEFT' | 'RIGHT';

export type Position = 'left' | 'right';

/** This object will store all product specific overrides for resizer component.
 * This had to be done since compiled styles need to be located in the same file as they're used. See existing examples for usage. */
export const resizerStyleOverrides = {
	ISSUE_VIEW_MARGIN_OFFSET: 'issue-view-margin-offset',
};

type PassthroughParameters = {
	style: {
		[key: string]: unknown;
	};
	ref: (ref: HTMLElement | null) => void;
};

export type HandlerProps = ResizerProps & {
	childrenContainerRef: {
		current: null | HTMLElement;
	};
};

export type ChildrenType = (props: PassthroughParameters) => ReactNode;

export type ResizerProps = {
	/** Proportion of parent size set on mount. Use to restore ratio */
	initialRatio?: number;
	/** Ratio to snap to when size is reset (double click resizer) */
	defaultRatio: number;
	/** Minimim size in pixels */
	minWidth?: number;
	/** Minimum ratio as proportion of parent size */
	minRatio?: number;
	/** Maximum size in pixels */
	maxWidth?: number;
	/** Maximum size as proportion of parent size */
	maxRatio?: number;
	/** Extra size added in pixels */
	extraWidth?: number;
	/** Resizer bar position */
	position?: Position;
	/** Chevron icon facing direction */
	chevronDirection?: Panel;
	/** Extract Handle component out of page flow so it is relative to resizing panel */
	resizeHandleAsOverlay?: boolean;
	/** z-index of Handle component */
	resizeHandleZIndex?: number;

	// left container of panel min width below which it cant be reduced
	minWidthLeftContainer?: number;

	// right container of panel min width below which it cant be reduced
	minWidthRightContainer?: number;

	/** Callback to listen for when the resizing starts
	 * onResizeStart will be called once when the resize handle is clicked.
	 * (Bug) However, when the resize handle is double clicked, it will be called twice as there
	 * have been two pointerup events. It will fire in the follwing order:
	 * onResizeStart > onRatioChange > onResizeStart > onRatioReset
	 */
	onResizeStart?: () => void;
	/** Callback to listen for resize changes. Use to persist ratio
	 * newWidth is the width of the resizable element
	 * setNextRatio is a function that force-sets the next ratio value of the resizable element. This is useful when the ratio is set from parent components.
	 */
	onRatioChange?: (
		newRatio: number,
		newWidth: number,
		setNextRatio: (ratio: number, setNextRatio?: number) => void,
	) => void;
	/** Callback to listen when reset to default size. Use to persist ratio */
	onRatioReset?: (newRatio: number) => void;
	/** Persist the resize icon when resizing */
	persistResizeIcon?: boolean;
	/** Callback to listen for when the resize icon is clicked. Return true if resize functionality should continue on click of resize handle
	 * setFlexBasisProp is a function that force-sets the flex-basis property of the resizable element.
	 */
	onResizeHandleClick?: (
		target: 'resizer' | 'chevron',
		setFlexBasisProp: (ratio: number, setNextRatio?: number) => void,
		event: PointerEvent<HTMLDivElement>,
	) => true | false;
	/** Collapse or expand the resizable element */
	collapsed?: boolean;
	/** Tooltip text shown on the resize handle chevron icon, preferrably i18n compliant */
	toolTipContent?: string;
	/** Allow consumers to style the resize handler and any other components */
	additionalStyles?: {
		resizeHandleStyles?: (typeof resizerStyleOverrides)[keyof typeof resizerStyleOverrides];
	};
};
